/**
 * Context menu for the 3D view.
 *
 * Follows the CAD convention (Rhino, AutoCAD, Navisworks): the right button
 * picks nothing, and every entry acts on the current selection. Entries that
 * don't apply to that selection are disabled rather than hidden, so the menu
 * reads the same every time.
 *
 * The canvas lives in the sandbox iframe, so it can't be the trigger itself:
 * the right-click arrives over the bridge and is replayed on a trigger that
 * covers the pane. That is what lets a menu in this document open at a point
 * clicked in another one.
 *
 * Adding an entry means pushing a `PartMenuAction` onto `partMenuActions`.
 */

import { useQuery } from "@tanstack/react-query";
import type { EditorSelection } from "forest/monaco-editor/MonacoEditor";
import { Copy, FileCode, type LucideIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { invariant } from "ts-invariant";

import type { SerializedNode } from "@/bear-sandbox/query";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  subscribeContextMenu,
  subscribeSelectionChange,
} from "@/lib/bear-sandbox-bridge";

import { sourceJumpTarget } from "./inspector-utils";
import { selectedNodesQuery } from "./selected-nodes-query";

/** What the menu acts on: the 3D selection, plus the ways to act on it. */
export type PartMenuContext = {
  nodeIds: string[];
  /** The selected nodes, empty while the sandbox query is in flight. */
  nodes: SerializedNode[];
  fileExists: (path: string) => boolean;
  openPath: (path: string, selection?: EditorSelection) => void;
};

export type PartMenuAction = {
  id: string;
  label: (context: PartMenuContext) => string;
  icon: LucideIcon;
  isEnabled: (context: PartMenuContext) => boolean;
  run: (context: PartMenuContext) => void;
};

/** The JSX call site of a single selected part, when its file is in the project. */
function jumpTarget(context: PartMenuContext) {
  const node = context.nodes.length === 1 ? context.nodes[0] : null;
  if (!node) return null;
  return sourceJumpTarget(node.source, context.fileExists);
}

/** Every action the part menu offers, in display order. */
export const partMenuActions: PartMenuAction[] = [
  {
    id: "copy-id",
    label: (context) =>
      context.nodeIds.length > 1 ? "Copy every id" : "Copy id",
    icon: Copy,
    isEnabled: (context) => context.nodeIds.length > 0,
    run: (context) => {
      void navigator.clipboard.writeText(context.nodeIds.join("\n"));
    },
  },
  {
    id: "jump-to-source",
    label: () => "Jump to source",
    icon: FileCode,
    isEnabled: (context) => jumpTarget(context) !== null,
    run: (context) => {
      const target = jumpTarget(context);
      invariant(target, "jump to source runs only when there is a call site");
      context.openPath(target.path, target.selection);
    },
  },
];

type BearPartMenuProps = Pick<PartMenuContext, "fileExists" | "openPath">;

const BearPartMenu = ({ fileExists, openPath }: BearPartMenuProps) => {
  const [nodeIds, setNodeIds] = useState<string[]>([]);
  const triggerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => subscribeSelectionChange(setNodeIds), []);

  // The trigger stands in for the canvas: it covers the pane the iframe fills,
  // so a right-click reported at (x, y) inside the iframe is the same point on
  // the trigger. Replaying it as a `contextmenu` event is what opens the menu,
  // and Radix reads the position it opens at off that event.
  useEffect(
    () =>
      subscribeContextMenu(({ x, y }) => {
        const trigger = triggerRef.current;
        invariant(trigger, "the menu trigger mounts with the pane");
        const canvas = trigger.getBoundingClientRect();
        trigger.dispatchEvent(
          new MouseEvent("contextmenu", {
            bubbles: true,
            clientX: canvas.left + x,
            clientY: canvas.top + y,
          }),
        );
      }),
    [],
  );

  const { data: nodes = [] } = useQuery(selectedNodesQuery(nodeIds));
  const context: PartMenuContext = { nodeIds, nodes, fileExists, openPath };

  return (
    <ContextMenu>
      <ContextMenuTrigger
        ref={triggerRef}
        className="pointer-events-none absolute inset-0"
      />
      <ContextMenuContent className="w-52">
        {partMenuActions.map((action) => (
          <ContextMenuItem
            key={action.id}
            disabled={!action.isEnabled(context)}
            onSelect={() => action.run(context)}
          >
            <action.icon />
            {action.label(context)}
          </ContextMenuItem>
        ))}
      </ContextMenuContent>
    </ContextMenu>
  );
};

export default BearPartMenu;
